import LinearProgress from 'components/@extended/LinearProgress';
import { ArrowOutwardRounded, VisibilityOff } from 'components/icons';
import { Tooltip } from 'components/ui/tooltip';
import {
    Fragment,
    HTMLAttributes,
    ReactNode,
    Ref,
    RefObject,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import type { FigureRow, WidgetFigure } from 'types/homeWidgetFigures';
import type { HomeWidgetDefinition, HomeWidgetPeriod, HomeWidgetSize } from 'types/homeWidgets';
import { cn } from 'utils/cn';
import { FigureFormat, formatChange, formatPercent } from './figureFormat';
import type { WidgetLinks } from './widgetLink';

/** What every form draws from. `links` is absent in a preview and for a family the viewer's catalogue lacks. */
export type KpiFormProps = {
    name: string;
    definition: HomeWidgetDefinition;
    figure: WidgetFigure;
    size: HomeWidgetSize;
    links?: WidgetLinks;
    format: FigureFormat;
    /** The figure is a row count that stopped at its list's cap: the list holds more. */
    capped?: boolean;
};

/** The headline figure's type. */
const FIGURE_CLASS = 'text-figure whitespace-nowrap tabular-nums';

/**
 * The headline's column beside a two-column chart: 190px, wider for a figure that needs it, and never so wide that
 * the chart is squeezed out.
 */
export const SIDE_CLASS = 'flex max-w-[60%] min-w-[190px] shrink-0 flex-col gap-1.5';

/** A number in a list or a legend. */
export const VALUE_CLASS = 'text-meta whitespace-nowrap tabular-nums';

const COMPARE: Partial<Record<HomeWidgetPeriod, string>> = {
    TODAY: 'home.widgets.compare.today',
    THIS_WEEK: 'home.widgets.compare.this-week',
    THIS_MONTH: 'home.widgets.compare.this-month',
    YEAR_TO_DATE: 'home.widgets.compare.year-to-date',
    LAST_12_MONTHS: 'home.widgets.compare.last-12-months',
};

/** A figure, a name or a row that opens what it stands for; plain text where it leads nowhere. */
export function FigureLink({
    to,
    className,
    ref,
    ...rest
}: { to?: string; ref?: Ref<HTMLElement> } & HTMLAttributes<HTMLElement>) {
    return to ? (
        <Link
            ref={ref as Ref<HTMLAnchorElement>}
            to={to}
            className={cn('text-foreground hover:text-link hover:underline', className)}
            {...rest}
        />
    ) : (
        <span ref={ref} className={className} {...rest} />
    );
}

/**
 * Which of `ways` of drawing the element its place holds: the first in which neither its own box nor its line
 * overflows, the last being taken unasked. It is drawn each way in turn to be asked, from the first again whenever
 * the card is resized, its line changes around it or the page's font arrives: what was drawn before never decides
 * the answer. A change inside an element fitted this way is no reason to ask, its own being the answer taking effect
 * and another's being drawn each way in its turn: two on one line would set each other off without end.
 */
function useFit(ref: RefObject<HTMLElement | null>, full: string, ways: number) {
    const [changes, setChanges] = useState(0);
    // The last way that was asked about, and what it answered.
    const [asked, setAsked] = useState({ at: '', way: 0, overflows: false });
    const at = `${changes}:${ways}:${full}`;
    const isAsked = asked.at === at;
    const way = isAsked ? asked.way + Number(asked.overflows) : 0;
    const isSettled = isAsked && (!asked.overflows || way === ways - 1);

    useEffect(() => {
        const line = ref.current?.parentElement;
        const card = line?.closest('section');
        if (!line || !card) return;
        const changed = () => setChanges((count) => count + 1);
        const resized = new ResizeObserver(changed);
        resized.observe(card);
        const rewritten = new MutationObserver((mutations) => {
            const isFitted = ({ target }: MutationRecord) =>
                (target instanceof Element ? target : target.parentElement)?.closest('[data-fitted]');
            if (!mutations.every(isFitted)) changed();
        });
        rewritten.observe(line, { childList: true, characterData: true, attributeFilter: ['class'], subtree: true });
        // The page's font is fetched: one that arrives after the first answer changes every width, and resizes nothing.
        void document.fonts?.ready.then(changed);
        return () => {
            resized.disconnect();
            rewritten.disconnect();
        };
    }, [ref]);

    useLayoutEffect(() => {
        const element = ref.current;
        const line = element?.parentElement;
        if (element && line && !isSettled) {
            const overflows = [element, line].some((box) => box.scrollWidth - box.clientWidth > 1);
            setAsked({ at, way, overflows });
        }
    }, [ref, at, way, isSettled]);

    return way;
}

/**
 * A text written in full where its place holds it and `short` where it does not, the full text staying what it is
 * called and what its tooltip says. It is the `peer` of its line and never gives way there, unless its `className`
 * lets it: what stands after it reads `data-short` off it. A form that has a roomier layout to offer says so, and
 * that is tried before the text is shortened: the form reads `data-cramped` off it.
 */
export function Fitted({
    full,
    short,
    to,
    roomier,
    className,
}: {
    full: string;
    short: string;
    to?: string;
    roomier?: boolean;
    className?: string;
}) {
    const ref = useRef<HTMLElement>(null);
    const [pointed, setPointed] = useState(false);
    const ways = roomier ? 3 : 2;
    const way = useFit(ref, full, ways);
    const isLast = way === ways - 1;
    // A small value reads the same short: the line still makes room for it, with nothing more to say about it.
    const isShort = isLast && short !== full;
    return (
        <Tooltip content={full} open={pointed && isShort} onOpenChange={setPointed}>
            <FigureLink
                ref={ref}
                to={to}
                data-fitted=""
                data-cramped={roomier && way > 0 ? '' : undefined}
                data-short={isLast ? '' : undefined}
                className={cn('peer max-w-full shrink-0 truncate', className)}
            >
                {isShort ? (
                    <>
                        <span aria-hidden>{short}</span>
                        <span className="sr-only">{full}</span>
                    </>
                ) : (
                    full
                )}
            </FigureLink>
        </Tooltip>
    );
}

/**
 * A value of the figure, fitted: in full, or as a bar's label is written, 46.2M EUR for EUR 46,196,158.52. The
 * headline figure unless `className` says otherwise.
 */
export function Headline({
    value,
    format,
    capped,
    className = FIGURE_CLASS,
    ...place
}: {
    value: number;
    format: FigureFormat;
    /** The value is a count that stopped at its list's cap. */
    capped?: boolean;
    to?: string;
    roomier?: boolean;
    className?: string;
}) {
    const more = capped ? '+' : '';
    return (
        <Fitted
            full={`${format(value)}${more}`}
            short={`${format(value, true)}${more}`}
            className={className}
            {...place}
        />
    );
}

/**
 * The figure against the same span of the period before: a neutral chip, since up is not always good, then what it
 * is compared with. Nothing without a figure to compare against, zero included: no share can be taken of it. The
 * two are one item of their line, and where it is too narrow for both side by side the words go under the chip.
 * Beside a figure in full the item keeps the width of its wider part, so both stay whole; beside one already
 * written short it keeps the widest chip's, and the words under it are cut to that.
 */
export function ChangeChip({ figure, period }: { figure: WidgetFigure; period: HomeWidgetPeriod }) {
    const { value, previousValue } = figure;
    const compareId = COMPARE[period];
    if (!previousValue || !compareId) return null;
    const change = (value - previousValue) / Math.abs(previousValue);
    return (
        <span className="flex min-w-min flex-wrap items-center gap-x-2 peer-data-[short]:min-w-18">
            <span className="bg-neutral-tint text-foreground-secondary text-caption flex h-5 shrink-0 items-center gap-0.5 rounded-full ps-1 pe-1.5 font-semibold whitespace-nowrap">
                {change === 0 ? null : (
                    <>
                        <ArrowOutwardRounded className={cn('size-3', change < 0 && 'rotate-90')} />
                        <span className="sr-only">
                            <FormattedMessage id={change > 0 ? 'home.widgets.change.up' : 'home.widgets.change.down'} />
                        </span>
                    </>
                )}
                {formatChange(Math.abs(change))}
            </span>
            <span className="text-muted-foreground text-caption min-w-0 truncate">
                <FormattedMessage id={compareId} />
            </span>
        </span>
    );
}

/** How far `value` is towards `target`, as a share that can pass 1. */
export function targetShare(value: number, target: number) {
    return target > 0 ? value / target : 0;
}

/**
 * A bar filled to `share`, full past 1. The text beside it says the same, so the bar itself is not announced.
 * `trackClassName` sets the track's height and ground.
 */
export function ShareBar({
    share,
    className,
    trackClassName,
}: {
    share: number;
    className?: string;
    trackClassName: string;
}) {
    return (
        <div aria-hidden className={className}>
            <LinearProgress
                variant="determinate"
                value={Math.min(Math.max(share, 0), 1) * 100}
                className={trackClassName}
            />
        </div>
    );
}

/**
 * The widget's total against its author's one target: a 4px bar and a line. Under a headline figure the line leads
 * with the share; a form with no headline passes its total as `lead`, and the line leads with that. The target is
 * written short where the line cannot hold it, as a fitted total leading the line is, so that the line's end is not
 * what gets cut. An element handed to a message as a value is one of a list of parts, hence its key.
 */
export function TargetStrip({
    figure,
    target,
    format,
    lead,
    className,
}: {
    figure: WidgetFigure;
    target: number;
    format: FigureFormat;
    lead?: ReactNode;
    className?: string;
}) {
    const share = targetShare(figure.value, target);
    const percent = formatPercent(share);
    const goal = <Fitted key="target" full={format(target)} short={format(target, true)} />;
    return (
        <div className={cn('flex flex-col gap-[3px]', className)}>
            <ShareBar share={share} trackClassName="bg-neutral-tint h-1" />
            <span className="text-muted-foreground text-caption truncate">
                {lead ? (
                    <FormattedMessage
                        id="home.widgets.target.value-of"
                        values={{ value: <Fragment key="lead">{lead}</Fragment>, target: goal, percent }}
                    />
                ) : (
                    <FormattedMessage
                        id="home.widgets.target.of"
                        values={{
                            percent: (
                                <strong key="percent" className="text-foreground font-semibold">
                                    {percent}
                                </strong>
                            ),
                            target: goal,
                        }}
                    />
                )}
            </span>
        </div>
    );
}

/** A row of a breakdown: one value of the dimension, or everything the drawn rows leave out. */
export type BreakdownRow = FigureRow & { isRest?: boolean };

/** The `top` largest rows, then one for everything else when there is anything else. */
export function topAndRest(figure: WidgetFigure, top: number): BreakdownRow[] {
    const rows = figure.rows ?? [];
    const rest = rows.slice(top).reduce((sum, row) => sum + row.value, figure.otherValue ?? 0);
    return rest > 0 ? [...rows.slice(0, top), { value: rest, isRest: true }] : rows.slice(0, top);
}

/** A row's name: its label, or what stands for the rest, or for the records with no value on the dimension. */
export function RowLabel({ row }: { row: BreakdownRow }) {
    return row.label ?? <FormattedMessage id={row.isRest ? 'home.widgets.other' : 'home.widgets.no-value'} />;
}

/** Where a row leads: the list narrowed to its value, and for the rest the list as the headline opens it. */
export function rowLink(links: WidgetLinks | undefined, row: BreakdownRow) {
    return row.isRest ? links?.list : links?.row(row);
}

/**
 * What stands in for a figure closed to the viewer, or for what `titleId` names, naming the access it needs when there
 * is one to name: `area`, already translated. In one row on a one-row card.
 */
export function ForbiddenState({
    area,
    titleId = 'home.widgets.state.forbidden.title',
    small = false,
    className,
}: {
    area?: string;
    titleId?: string;
    small?: boolean;
    className?: string;
}) {
    return (
        <div
            className={cn(
                'text-meta flex min-h-0 flex-1 items-center gap-2.5',
                !small && 'flex-col justify-center gap-1 text-center',
                className
            )}
        >
            <VisibilityOff className={cn('text-muted-foreground', small ? 'size-[18px]' : 'size-6')} />
            <div className="flex min-w-0 flex-col">
                <strong className="text-foreground font-semibold">
                    <FormattedMessage id={titleId} />
                </strong>
                {area ? (
                    <span className="text-muted-foreground">
                        <FormattedMessage id="home.widgets.state.forbidden.body" values={{ area }} />
                    </span>
                ) : null}
            </div>
        </div>
    );
}
